import { CameraControls, PerspectiveCamera } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';

import ContactShadow from '@cartwhl/3d/r3f/components/ContactShadow';
import Lights from '@cartwhl/3d/r3f/components/Lights';

import {
  CARTOGRAPHER_VIEWPORT_FLOOR_HEX_COLOR,
  CARTOGRAPHER_VIEWPORT_GRADIENT_STOPS,
  CARTOGRAPHER_VIEWPORT_SKY_HEX_COLOR,
} from '../constants';

const CAMERA_MAX_DISTANCE_FRAMED_RATIO = 10;
const CAMERA_MAX_DISTANCE_RADIUS_RATIO = 24;
const CAMERA_MAX_DISTANCE_MIN = 10;
const BACKDROP_CUBE_FACE_SIZE = 1024;

export const CARTOGRAPHER_DEFAULT_CAMERA_POSITION: [number, number, number] = [
  0, 1, 10,
];

function parseGradientStop(percent: string, fallback: number): number {
  const value = Number.parseFloat(percent);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value / 100));
}

function createSolidBackdropFace(hexColor: string): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = BACKDROP_CUBE_FACE_SIZE;
  canvas.height = BACKDROP_CUBE_FACE_SIZE;
  if (!context) return null;

  context.fillStyle = hexColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  return canvas;
}

function createGradientBackdropFace(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = BACKDROP_CUBE_FACE_SIZE;
  canvas.height = BACKDROP_CUBE_FACE_SIZE;
  if (!context) return null;

  const baseSkyHoldEnd = parseGradientStop(
    CARTOGRAPHER_VIEWPORT_GRADIENT_STOPS.skyHoldEnd,
    0.42,
  );
  const baseFloorStart = parseGradientStop(
    CARTOGRAPHER_VIEWPORT_GRADIENT_STOPS.floorStart,
    0.7,
  );
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, CARTOGRAPHER_VIEWPORT_SKY_HEX_COLOR);
  gradient.addColorStop(baseSkyHoldEnd, CARTOGRAPHER_VIEWPORT_SKY_HEX_COLOR);
  gradient.addColorStop(baseFloorStart, CARTOGRAPHER_VIEWPORT_FLOOR_HEX_COLOR);
  gradient.addColorStop(1, CARTOGRAPHER_VIEWPORT_FLOOR_HEX_COLOR);
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  return canvas;
}

function createCartographerEnvironmentTexture(): THREE.CubeTexture | null {
  const sideFace = createGradientBackdropFace();
  const skyFace = createSolidBackdropFace(CARTOGRAPHER_VIEWPORT_SKY_HEX_COLOR);
  const floorFace = createSolidBackdropFace(
    CARTOGRAPHER_VIEWPORT_FLOOR_HEX_COLOR,
  );
  if (!sideFace || !skyFace || !floorFace) return null;

  // Mirror the shared Backdrop cube while letting Three render it as an
  // infinite cubemap, so there is no finite geometry to clip into view.
  const texture = new THREE.CubeTexture([
    sideFace,
    sideFace,
    skyFace,
    floorFace,
    sideFace,
    sideFace,
  ]);
  texture.colorSpace = THREE.NoColorSpace;
  //texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function CartographerEnvironmentBackground() {
  const scene = useThree((state) => state.scene);
  const environmentTexture = useMemo(
    () => createCartographerEnvironmentTexture(),
    [],
  );

  useEffect(() => {
    if (!environmentTexture) return undefined;
    const previousBackground = scene.background;
    scene.background = environmentTexture;

    return () => {
      if (scene.background === environmentTexture) {
        scene.background = previousBackground;
      }
      environmentTexture.dispose();
    };
  }, [environmentTexture, scene]);

  return null;
}

function CartographerSceneLighting({ showShadow }: { showShadow: boolean }) {
  return (
    <>
      <Lights />
      {showShadow && <ContactShadow />}
    </>
  );
}

interface CartographerSceneSetupProps {
  showShadow: boolean;
  sceneRadius?: number;
  sceneFrameDistance?: number | null;
  /** Hide backdrop/lights until auto-framing settles to prevent visual pop-in. */
  contentVisible?: boolean;
}

export default function CartographerSceneSetup({
  showShadow,
  sceneRadius,
  sceneFrameDistance,
  contentVisible = true,
}: CartographerSceneSetupProps) {
  const effectiveSceneRadius =
    sceneRadius && Number.isFinite(sceneRadius) ? sceneRadius : 5;
  const hasFramedDistance =
    sceneFrameDistance !== null &&
    sceneFrameDistance !== undefined &&
    Number.isFinite(sceneFrameDistance) &&
    sceneFrameDistance > 0;
  const cameraMaxDistance = hasFramedDistance
    ? sceneFrameDistance * CAMERA_MAX_DISTANCE_FRAMED_RATIO
    : Math.max(
        effectiveSceneRadius * CAMERA_MAX_DISTANCE_RADIUS_RATIO,
        CAMERA_MAX_DISTANCE_MIN,
      );
  return (
    <>
      <PerspectiveCamera
        makeDefault
        position={CARTOGRAPHER_DEFAULT_CAMERA_POSITION}
        zoom={1}
        near={0.01}
        far={100000}
      />
      <CameraControls makeDefault maxDistance={cameraMaxDistance} />
      <CartographerEnvironmentBackground />
      <group visible={contentVisible}>
        <CartographerSceneLighting showShadow={showShadow} />
      </group>
    </>
  );
}
